import React from 'react';
import {
    Box,
    Button,
    Paper,
    Typography,
    useTheme,
} from '@mui/material';
import { FlagIcon } from 'src/components/iconify/flagIcon';
import HorizontalPager from 'src/components/horizontal-pager';
import PulseDot from 'src/components/pulse-dot';
import { CountryFilterProps, CountryItem } from '../types';
import { getLevelColor } from '../utils';
import countriesCatalog from 'src/utils/countries.json';

export function CountryFilter({ 
    countries, 
    selectedCountry, 
    onCountryChange,
    backgroundColor = 'transparent',
    enableDeepLinking = true,
    renderCountryLabel
}: CountryFilterProps) {
    const theme = useTheme();

    if (countries.length <= 1) {
        return null;
    }

    const nameToCode = React.useMemo(() => {
        const map = new Map<string, string>();
        countriesCatalog.forEach((c: { name: string; code: string }) => {
            map.set(c.name.toLowerCase(), c.code.toLowerCase());
        });
        return map;
    }, []);

    const codeToName = React.useMemo(() => {
        const map = new Map<string, string>();
        countries.forEach((c) => {
            map.set(String(c.country_code).toUpperCase(), c.country_name);
        });
        return map;
    }, [countries]);

    const [didApplyDeepLink, setDidApplyDeepLink] = React.useState(false);
    React.useEffect(() => {
        if (didApplyDeepLink || !enableDeepLinking) return;
        try {
            const params = new URLSearchParams(window.location.search);
            const codeParam = params.get('country');
            if (codeParam) {
                const countryName = codeToName.get(codeParam.toUpperCase());
                if (countryName) {
                    onCountryChange(selectedCountry === countryName ? selectedCountry : countryName);
                }
            }
        } catch {}
        setDidApplyDeepLink(true);
    }, [codeToName, onCountryChange, didApplyDeepLink, selectedCountry, enableDeepLinking]);

    const getCountryOpacity = (countryName: string) => {
        return selectedCountry === countryName ? 1 : 0.5;
    };

    return (
        <Paper sx={{ p: 0, overflow: 'hidden', backgroundColor: backgroundColor, borderBottom: "1px solid rgba(145, 158, 171, 0.20)", px: '24px'}}>
            <HorizontalPager
                items={countries}
                getItemKey={(c) => c.country_code}
                activeIndex={selectedCountry ? countries.findIndex((c) => c.country_name === selectedCountry) : null}
                renderItem={(c: CountryItem) => (
                    <Button
                        key={c.country_code}
                        variant="text"
                        color="inherit"
                        disableRipple
                        disableFocusRipple
                        disableTouchRipple
                        onClick={() => onCountryChange(selectedCountry === c.country_name ? null : c.country_name)}
                        sx={{
                            px: 2,
                            gap: 1.2,
                            minHeight: 36,
                            borderRadius: 0,
                            position: 'relative',
                            '&::after': {
                                content: '""',
                                position: 'absolute',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                bottom: 0,
                                height: '2px',
                                width: selectedCountry === c.country_name ? '70%' : 0,
                                backgroundColor: theme.palette.mode === 'dark' ? '#fff' : '#000',
                            },
                            '&:hover': { backgroundColor: 'transparent' },
                        }}
                    >
                        {nameToCode.has(c.country_name.toLowerCase()) && (
                            <FlagIcon code={nameToCode.get(c.country_name.toLowerCase())!} />
                        )}
                        <Typography variant="body2" lineHeight="18px" textAlign="center" whiteSpace="nowrap" sx={{ opacity: getCountryOpacity(c.country_name) }}>
                            {renderCountryLabel ? renderCountryLabel(c, selectedCountry === c.country_name) : c.country_name}
                        </Typography>
                        <PulseDot color={c.level ? getLevelColor(c.level, theme) : '#fff'} size={18} />
                    </Button>
                )}
            />
        </Paper>
    );
}
